/**
 * Mobile shell ⇄ WebView postMessage bridge (W-X-06 / W-X-07).
 *
 * Implements the WebView→Shell side of the contract in mobile-architecture.md §4 /
 * data-contract.md §3.2. The app posts JSON messages to the Expo shell via
 * `window.ReactNativeWebView.postMessage`. When not inside a WebView these are no-ops.
 */

interface ReactNativeWebView {
  postMessage: (msg: string) => void;
}

declare global {
  interface Window {
    ReactNativeWebView?: ReactNativeWebView;
    __DP_WEBVIEW__?: boolean;
  }
}

/** W-X-07: detect whether we're running inside the Expo WebView shell. */
export function isInWebView(): boolean {
  return (
    typeof window !== "undefined" &&
    (window.__DP_WEBVIEW__ === true || typeof window.ReactNativeWebView !== "undefined")
  );
}

// WebView → Shell message types (data-contract.md §3.2).
export type ShellMessage =
  | { type: "auth.login"; user_id: string; role: string; access_token?: string; refresh_token?: string }
  | { type: "auth.logout" }
  | { type: "auth.require_otp"; purpose: "govt_id_export" }
  | { type: "nav.title"; title: string }
  | { type: "request.camera"; purpose: "rx_upload" | "govt_id" | "support" }
  | { type: "request.file"; purpose: string; mimes: string[] }
  | { type: "request.tel"; phone: string };

export function postToShell(message: ShellMessage): void {
  if (typeof window === "undefined" || !window.ReactNativeWebView) return;
  window.ReactNativeWebView.postMessage(JSON.stringify(message));
}

/** Subscribe to Shell → WebView messages (injected via injectJavaScript). */
export function onShellMessage(handler: (data: unknown) => void): () => void {
  const listener = (event: MessageEvent) => {
    try {
      handler(JSON.parse(event.data));
    } catch {
      /* ignore non-JSON messages */
    }
  };
  window.addEventListener("message", listener);
  return () => window.removeEventListener("message", listener);
}
